import {TextEditorServices} from '@frontapp/editor-html';
import React, {Component} from 'react';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DraftMessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ToolbarButton} from '../../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {AnswerModel} from '../../../../../models/api/answerModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {QuickResponses} from '../../../../../models/api/preferencesModel';
import {VariableInterpolator} from '../../../../../services/variableInterpolation/variableInterpolator';
import {AnswerDialogContainer} from '../../answers/answerDialogContainer';
import {AnswersDropdownContainer} from '../../answers/answersDropdownContainer';

/*
 * Props.
 */

export interface AnswersToolbarButtonProps {
  channelId: number;
  message?: DraftMessageModel;
  namespace: string;
  onAnswerSelect?: (model: AnswerModel) => void;
  conversation: ConversationModel | null;
  interpolator: VariableInterpolator;
  editorServices: TextEditorServices;
}

interface AnswersToolbarButtonPrivateProps extends AnswersToolbarButtonProps {
  quickResponses: QuickResponses;
}

/*
 * Component.
 */

export interface AnswersToolbarButtonState {
  isCreateOpen: boolean;
}

export class AnswersToolbarButton extends Component<
  AnswersToolbarButtonPrivateProps,
  AnswersToolbarButtonState
> {
  constructor(props: AnswersToolbarButtonPrivateProps) {
    super(props);
    this.state = {isCreateOpen: false};
  }

  /*
   * Event Handlers.
   */

  private readonly toggleIsCreateOpen = () => {
    this.setState(({isCreateOpen}) => ({isCreateOpen: !isCreateOpen}));
  };

  private readonly selectQuickResponse: (arg: number) => void = (index) => {
    const {onAnswerSelect, message} = this.props;
    const answer = this.props.quickResponses[index];

    if (!answer || !onAnswerSelect) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.CANNED_ANSWER_INSERT, {
      origin: 'hotkey',
      id: answer.id,
      folder_id: answer.folderId,
      in_folder: Boolean(answer.folderId),
      message_uid: message?.uid,
    });

    onAnswerSelect(answer);
  };

  /*
   * Render.
   */

  render() {
    const handlers = {
      [KeyboardActionsEnum.QUICK_RESPONSE_1]: () => this.selectQuickResponse(0),
      [KeyboardActionsEnum.QUICK_RESPONSE_2]: () => this.selectQuickResponse(1),
      [KeyboardActionsEnum.QUICK_RESPONSE_3]: () => this.selectQuickResponse(2),
      [KeyboardActionsEnum.QUICK_RESPONSE_4]: () => this.selectQuickResponse(3),
      [KeyboardActionsEnum.QUICK_RESPONSE_5]: () => this.selectQuickResponse(4),
    };
    return (
      <>
        <KeyboardShortcuts handlers={handlers} />
        {this.renderButtonAndDropdown()}
        {this.maybeRenderCreateDialog()}
      </>
    );
  }

  private renderButtonAndDropdown() {
    return (
      <PopoverCoordinator
        renderButton={(isOpen) => renderButton(isOpen)}
        renderModal={(modalProps) => renderDropdown(modalProps, this.props, this.toggleIsCreateOpen)}
        keyboardAction={KeyboardActionsEnum.EDITOR_ANSWERS}
      />
    );
  }

  /*
   * Creation dialog.
   */

  private maybeRenderCreateDialog() {
    const {namespace, message} = this.props;

    if (!this.state.isCreateOpen || !message) {
      return null;
    }

    return (
      <AnswerDialogContainer
        namespace={namespace}
        message={message}
        onRequestClose={this.toggleIsCreateOpen}
        isFromDraft
      />
    );
  }
}

/*
 * Button + Dropdown.
 */

function renderButton(isOpen: boolean) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          interactionId="message-template-toolbar-button"
          hasHover={isOpen}
          iconName="messageTemplate"
          label={<Trans id="composer-cannedresponses">Message templates</Trans>}
          keyboardAction={KeyboardActionsEnum.EDITOR_ANSWERS}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderDropdown(
  modalProps: PopoverTransferProps,
  props: AnswersToolbarButtonPrivateProps,
  toggleIsCreateOpen: () => void,
) {
  return (
    <AnswersDropdownContainer
      {...props}
      {...modalProps}
      channelId={props.channelId}
      onCreateClick={toggleIsCreateOpen}
    />
  );
}
